import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { CatalogPartDataGridApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartDataGridApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { CatalogPartView } from "src/api/generated/erp/db/types/tables/catalogPartView.ts";
import { textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { ProductChangeRequestCatalogPartsApi } from "src/api/generated/erp/productChanges/api/productChangeRequestCatalogPartsApi.ts";
import { LazySelectField } from "src/components/common/inputFields/LazySelectField.tsx";
import { PartCategoryApi } from "src/api/generated/erp/parts/categories/api/partCategoryApi.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ConnectCatalogPartsToProductChangeRequestDialogProps {
	productChangeRequestId: number;
	onCompleted: () => Promise<void>;
}

export const ConnectCatalogPartsToProductChangeRequestDialog = ({
	productChangeRequestId,
	onCompleted,
}: ConnectCatalogPartsToProductChangeRequestDialogProps) => {
	return (
		<ServerSideDataGridModel
			gridId={"B3FA0FEE2F4B6B1C"}
			initialParams={{
				searchQuery: "",
				partCategoryId: genericNullableValue<number>(null),
			}}
			fetchData={({ searchQuery, partCategoryId, dataModelRequest }) =>
				CatalogPartDataGridApi.searchCatalogParts({
					searchQuery,
					partCategoryId,
					dataModelRequest,
				})
			}
			getDataModelResult={(data) => data}
			getRowId={(row) => row.catalogPartId}
			render={({ refreshData, dataGridProps, selectedRows }) => (
				<AavoDataGrid<CatalogPartView>
					columns={[
						textColumn({
							field: "partNo",
							headerName: i18n.t("part_no"),
							width: 160,
						}),
						textColumn({
							field: "description_1",
							headerName: i18n.t("description"),
							width: 240,
						}),
						textColumn({
							field: "categoryName",
							headerName: i18n.t("category"),
							width: 160,
						}),
					]}
					actionBarComponents={
						<>
							<AavoTextField
								label={i18n.t("search")}
								onSubmit={async (value) => {
									await refreshData({ searchQuery: value });
								}}
							/>
							<LazySelectField
								label={i18n.t("category")}
								fetchOptions={PartCategoryApi.getAll}
								getOptionKey={(option) => option.partCategoryId}
								getOptionLabel={(option) => option.categoryName}
								onChange={async (key) => {
									await refreshData({ partCategoryId: key });
								}}
							/>
							<AsyncButton
								icon={faLink}
								label={i18n.t("connect")}
								variant={"outlined"}
								disabled={selectedRows.length === 0}
								onClick={async () => {
									await ProductChangeRequestCatalogPartsApi.connectCatalogParts({
										productChangeRequestId,
										connections: selectedRows.map((r) => ({
											catalogPartId: r.catalogPartId,
											impact: "",
										})),
									});
									await onCompleted();
								}}
							/>
						</>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.productChanges.catalogPartsToConnect.grid"),
						data: {
							productChangeRequestId: productChangeRequestId,
						},
						rowFields: ["catalogPartId", "partNo", "description_1", "categoryName"],
						selectedRowPathSegment: (row) => row.partNo,
						selectedRowsPathSegment: (count) => i18n.t("catalog_parts_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
